import { cn } from '@/lib/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode, ButtonHTMLAttributes, KeyboardEvent } from 'react';
import { useHaptics } from '@/hooks/use-haptics';
import { useSound } from '@/hooks/useSound';
import { ChevronDown } from 'lucide-react';

/* ─── Card ───────────────────────────────────────────────────── */

interface CardProps {
    children: ReactNode;
    className?: string;
    onClick?: () => void;
    style?: React.CSSProperties;
}

export function Card({ children, className, onClick, style }: CardProps) {
    const { play } = useSound();
    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            play('ui_press');
            onClick();
        }
    }, [onClick, play]);

    return (
        <div
            onClick={() => {
                if (onClick) {
                    play('ui_press');
                    onClick();
                }
            }}
            onMouseEnter={() => onClick && play('ui_hover')}
            onKeyDown={handleKeyDown}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            style={style}
            className={cn(
                'palaestra-card p-[24px] bg-bg-elevated border border-white/[0.05] shadow-card-elite rounded-card',
                'transition-[transform,box-shadow,border-color] duration-[var(--dur-base)] ease-[var(--ease-out)]',
                onClick && 'palaestra-card-interactive active:translate-y-[1px] active:scale-[0.995] hover:border-gold-glow/30 transition-all duration-300',
                className
            )}
        >
            {children}
        </div>
    );
}

/* ─── Button ─────────────────────────────────────────────────── */

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'lg' | 'icon';
    isHero?: boolean;
}

export function Button({ variant = 'primary', size = 'md', isHero = false, className, children, onClick, ...props }: ButtonProps) {
    const { play } = useSound();
    return (
        <button
            onClick={(e) => {
                play('ui_press');
                onClick?.(e);
            }}
            onMouseEnter={() => !props.disabled && play('ui_hover')}
            className={cn(
                'font-semibold transition-all duration-[var(--dur-fast)] ease-[var(--ease-snap)] active:scale-[0.98] active:translate-y-[1px] disabled:opacity-40 disabled:pointer-events-none inline-flex items-center justify-center gap-2 relative overflow-hidden min-h-[44px]',
                {
                    'bg-gradient-gold-elite text-bg-900 rounded-button shadow-button-elite hover:shadow-[0_12px_24px_rgba(212,175,55,0.3)] hover:brightness-110 uppercase tracking-wide': variant === 'primary',
                    'bg-transparent text-gold-base rounded-button border border-white/[0.1] hover:border-gold-base/50 hover:bg-gold-base/5': variant === 'secondary',
                    'bg-transparent hover:bg-white/[0.05] text-text-secondary hover:text-text-primary rounded-button': variant === 'ghost',
                    'bg-[#3A0E0E] text-[#FCA5A5] border border-[#7F1D1D] rounded-button hover:shadow-[0_0_12px_rgba(153,27,27,0.4)]': variant === 'danger',
                },
                {
                    'text-micro px-3 py-1.5 h-[32px] min-h-0': size === 'sm',
                    'text-label px-6 py-3 h-[48px]': size === 'md',
                    'text-[18px] px-8 py-4 h-[56px]': size === 'lg',
                    'w-[56px] h-[56px] p-0': size === 'icon',
                },
                isHero && 'motion-safe:animate-[eliteBreathe_3s_var(--ease-inout)_infinite]',
                className
            )}
            {...props}
        >
            {children}
        </button>
    );
}

/* ─── Chip ───────────────────────────────────────────────────── */

interface ChipProps {
    label: string;
    active?: boolean;
    onClick?: () => void;
    variant?: 'default' | 'locked' | 'available' | 'worked' | 'mastered';
}

const chipColors = {
    default: 'bg-bg-700 text-text-secondary border-white/[0.05]',
    locked: 'bg-bg-800 text-text-muted border-white/[0.03]',
    available: 'bg-category-bottom/10 text-category-bottom border-category-bottom/20',
    worked: 'bg-category-conditioning/10 text-category-conditioning border-category-conditioning/20',
    mastered: 'bg-category-top/10 text-category-top border-category-top/20',
};

export function Chip({ label, active, onClick, variant = 'default' }: ChipProps) {
    const { play } = useSound();
    return (
        <button
            type="button"
            onClick={() => {
                if (onClick) {
                    play('ui_press');
                    onClick();
                }
            }}
            onMouseEnter={() => onClick && !active && play('ui_hover')}
            aria-pressed={active}
            className={cn(
                'text-[12px] font-medium px-4 py-2 rounded-full border transition-all duration-[var(--dur-base)] ease-[var(--ease-out)] min-h-[44px] flex items-center justify-center',
                'active:scale-[0.97] active:translate-y-[1px]',
                active
                    ? 'bg-gold-base text-bg-900 border-gold-base shadow-[0_0_15px_rgba(212,175,55,0.25)]'
                    : chipColors[variant],
                onClick && !active && 'hover:bg-bg-600 hover:border-white/20'
            )}
        >
            {label}
        </button>
    );
}

/* ─── Stepper ────────────────────────────────────────────────── */

interface StepperProps {
    label: string;
    value: number;
    onChange: (v: number) => void;
    min?: number;
    max?: number;
    step?: number;
    formatValue?: (v: number) => string;
}

export function Stepper({ label, value, onChange, min = 1, max = 999, step = 1, formatValue }: StepperProps) {
    const { trigger, patterns } = useHaptics();
    const { play } = useSound();
    const [shake, setShake] = useState<'left' | 'right' | null>(null);
    const display = formatValue ? formatValue(value) : String(value);

    const update = (newValue: number, direction: 'left' | 'right') => {
        if (newValue < min || newValue > max) {
            trigger(patterns.warning);
            setShake(direction);
            setTimeout(() => setShake(null), 400);
            return;
        }
        trigger(patterns.light);
        play('ui_press');
        onChange(newValue);
    };

    return (
        <div className="flex items-center justify-between min-h-[44px]">
            <span className="text-text-secondary font-medium">{label}</span>
            <div className={cn("flex items-center gap-3 transition-transform", shake === 'left' ? 'animate-shake' : shake === 'right' ? 'animate-shake' : '')}>
                <button
                    type="button"
                    onClick={() => update(value - step, 'left')}
                    disabled={value <= min}
                    className="w-11 h-11 rounded-[14px] bg-bg-700 border border-white/[0.05] flex items-center justify-center text-text-primary hover:text-gold-400 active:scale-90 disabled:opacity-30 disabled:active:scale-100 transition-all text-lg"
                >
                    −
                </button>
                <span className="w-14 text-center font-bold text-gold-500 tabular-nums">{display}</span>
                <button
                    type="button"
                    disabled={value >= max}
                    onClick={() => update(value + step, 'right')}
                    className="w-11 h-11 rounded-[14px] bg-bg-700 border border-white/[0.05] flex items-center justify-center text-text-primary hover:text-gold-400 active:scale-90 disabled:opacity-30 disabled:active:scale-100 transition-all text-lg"
                >
                    +
                </button>
            </div>
        </div>
    );
}

/* ─── Toggle ─────────────────────────────────────────────────── */

interface ToggleProps {
    label: string;
    checked: boolean;
    onChange: (v: boolean) => void;
}

export function Toggle({ label, checked, onChange }: ToggleProps) {
    const { trigger, patterns } = useHaptics();
    const { play } = useSound();
    return (
        <label className="flex items-center justify-between cursor-pointer group min-h-[44px]">
            <span className="text-text-secondary group-hover:text-text-primary transition-colors">{label}</span>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                onClick={() => {
                    trigger(patterns.light);
                    play('ui_press');
                    onChange(!checked);
                }}
                className={cn(
                    'relative w-12 h-7 rounded-full transition-all duration-180',
                    checked
                        ? 'bg-gold-500 shadow-elevation-1'
                        : 'bg-bg-800 border border-white/[0.05]'
                )}
            >
                <span
                    className={cn(
                        'absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-all duration-180',
                        checked && 'translate-x-5'
                    )}
                />
            </button>
        </label>
    );
}

/* ─── Progress Ring ──────────────────────────────────────────── */

interface ProgressRingProps {
    value: number;       // 0–100
    size?: number;
    strokeWidth?: number;
    className?: string;
    children?: ReactNode;
    color?: string;
    isActive?: boolean;
}

export function ProgressRing({ value, size = 120, strokeWidth = 6, className, children, color, isActive }: ProgressRingProps) {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (Math.min(value, 100) / 100) * circumference;

    return (
        <div
            className={cn('relative inline-flex items-center justify-center rounded-full', className, isActive && 'animate-pulse-glow')}
            role="progressbar"
            aria-valuenow={Math.round(Math.min(value, 100))}
            aria-valuemin={0}
            aria-valuemax={100}
        >
            <svg width={size} height={size} className="-rotate-90">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#1C2430"
                    strokeWidth={strokeWidth}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color ?? 'url(#gold-gradient-ring)'}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    className="progress-ring-circle drop-shadow-[0_0_12px_rgba(201,162,39,0.35)]"
                />
                <defs>
                    <linearGradient id="gold-gradient-ring" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#F4D675" />
                        <stop offset="100%" stopColor="#C9A227" />
                    </linearGradient>
                </defs>
            </svg>
            {
                children && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        {children}
                    </div>
                )
            }
        </div >
    );
}

/* ─── Animated Number ────────────────────────────────────────── */

interface AnimatedNumberProps {
    value: number;
    className?: string;
    suffix?: string;
}

export function AnimatedNumber({ value, className, suffix = '' }: AnimatedNumberProps) {
    const [display, setDisplay] = useState(0);
    const prevRef = useRef(0);

    useEffect(() => {
        const start = prevRef.current;
        const end = value;
        const duration = 600;
        const startTime = performance.now();

        const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
            setDisplay(Math.round(start + (end - start) * eased));
            if (progress < 1) requestAnimationFrame(animate);
        };

        requestAnimationFrame(animate);
        prevRef.current = end;
    }, [value]);

    return (
        <span className={cn('tabular-nums font-semibold', className)}>
            {display}{suffix}
        </span>
    );
}

/* ─── Section Header ─────────────────────────────────────────── */

interface SectionHeaderProps {
    title: string;
    subtitle?: string;
    className?: string;
    action?: ReactNode;
}

export function SectionHeader({ title, subtitle, className, action }: SectionHeaderProps) {
    return (
        <div className={cn('flex items-end justify-between', className)}>
            <div>
                <h2 className="text-text-primary">{title}</h2>
                {subtitle && <p className="text-text-muted mt-0.5">{subtitle}</p>}
            </div>
            {action}
        </div>
    );
}

/* ─── Stat Card ──────────────────────────────────────────────── */

interface StatCardProps {
    icon: ReactNode;
    label: string;
    value: number;
    suffix?: string;
    className?: string;
    delay?: number;
}

export function StatCard({ icon, label, value, suffix = '', className, delay = 0 }: StatCardProps) {
    return (
        <Card className={cn('text-center py-6', className)} >
            <div className="animate-fade-in-up" style={{ animationDelay: `${delay}ms`, animationFillMode: 'backwards' }}>
                <div className="flex items-center justify-center mb-3">
                    <div className="w-10 h-10 rounded-xl bg-gold-100 flex items-center justify-center">
                        {icon}
                    </div>
                </div>
                <p className="text-2xl font-bold text-text-primary">
                    <AnimatedNumber value={value} suffix={suffix} />
                </p>
                <p className="text-[12px] text-text-muted mt-1 uppercase tracking-wider font-medium">{label}</p>
            </div>
        </Card>
    );
}

/* ─── Empty State ────────────────────────────────────────────── */

interface EmptyStateProps {
    icon: ReactNode;
    title: string;
    subtitle?: string;
    action?: {
        label: string;
        onClick: () => void;
    };
}

export function EmptyState({ icon, title, subtitle, action }: EmptyStateProps) {
    return (
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center animate-fade-in">
            <div className="w-16 h-16 rounded-3xl bg-bg-800 flex items-center justify-center text-text-muted mb-4 border border-white/[0.03]">
                {icon}
            </div>
            <h3 className="text-text-primary mb-1">{title}</h3>
            {subtitle && <p className="text-text-secondary mb-6 max-w-[240px]">{subtitle}</p>}
            {action && (
                <Button onClick={action.onClick} variant="secondary" size="md">
                    {action.label}
                </Button>
            )}
        </div>
    );
}

/* ─── Select ─────────────────────────────────────────────────── */

export function Select<T extends string | number>({
    value,
    onChange,
    options,
    placeholder = 'Select...',
    className
}: {
    value: T | null;
    onChange: (val: T) => void;
    options: { label: string; value: T }[];
    placeholder?: string;
    className?: string;
}) {
    const { play } = useSound();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value);

    return (
        <div className={cn("relative", className)} ref={containerRef}>
            <button
                type="button"
                onClick={() => {
                    play('ui_press');
                    setIsOpen(!isOpen);
                }}
                className={cn(
                    "w-full h-[48px] px-4 flex items-center justify-between",
                    "bg-bg-elevated border border-white/[0.05] rounded-xl",
                    "text-sm font-medium transition-all duration-200",
                    isOpen ? "border-gold-base/50 shadow-[0_0_12px_rgba(212,175,55,0.1)]" : "hover:border-white/10",
                    !selectedOption ? "text-text-muted" : "text-text-primary"
                )}
            >
                <span className="truncate">
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDown
                    className={cn(
                        "w-4 h-4 text-text-muted transition-transform duration-200",
                        isOpen && "rotate-180 text-gold-base"
                    )}
                />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 py-1 z-[9999] bg-[#0E1420] border border-white/20 rounded-xl shadow-2xl ring-1 ring-white/10">
                    <div className="max-h-[240px] overflow-y-auto">
                        {options.length === 0 && (
                            <div className="px-4 py-3 text-sm text-text-muted">No options available</div>
                        )}
                        {options.map((option) => (
                            <button
                                key={String(option.value)}
                                type="button"
                                onClick={() => {
                                    play('ui_press');
                                    onChange(option.value);
                                    setIsOpen(false);
                                }}
                                onMouseEnter={() => play('ui_hover')}
                                className={cn(
                                    "w-full text-left px-4 py-3 text-sm transition-colors",
                                    option.value === value
                                        ? "text-gold-base bg-gold-base/[0.08]"
                                        : "text-text-secondary hover:text-text-primary hover:bg-white/[0.04]"
                                )}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
